import { Button, Flex, useToast } from '@chakra-ui/react';
import { useContext, useEffect } from 'react';
import { BrowserView, MobileView } from 'react-device-detect';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { AuthenticatedComponent } from '../auth/keycloak';
import { ProfileInfoComponent } from '../component/setupComponents/ProfileInfoComponent';
import { StreamsixBanner } from '../component/setupComponents/StreamsixBanner';
import { UploadImageComponent } from '../component/setupComponents/UploadImageComponent';
import PageDataContext from '../context/PageDataContext';
import { usePageDataCache } from '../hooks/usePageDataCache';
import {
    deleteAvatar,
    getUserPrivateProfile,
    setUserPrivateProfile,
    uploadAvatar,
    UserProfile,
} from '../service/social';
import { getFileFromURL } from '../util/util';

export const SetupProfilePage: AuthenticatedComponent = ({ keycloak }) => {
    const localStoragePrefix = `ManageProfile`;
    const intl = useIntl();
    const [profilePictureURL, setProfilePictureURL] = usePageDataCache(
        `${localStoragePrefix}_profilePictureURL`,
        '',
    );
    const [profile, setProfile] = usePageDataCache<UserProfile | undefined>(
        `${localStoragePrefix}_profile`,
        undefined,
    );
    const [aboutMe, setAboutMe] = usePageDataCache(`${localStoragePrefix}_aboutMe`, '');
    const [username, setUsername] = usePageDataCache(`${localStoragePrefix}_username`, '');

    const { isLoadFromCache } = useContext(PageDataContext);
    const toast = useToast();
    const navigate = useNavigate();

    const onConfirmDetails = async () => {
        const profile = await getUserPrivateProfile(keycloak.subject, keycloak.token);
        if (!profile) {
            toast({
                title: intl.formatMessage({
                    id: 'global.error_occurred',
                    defaultMessage: 'An error occurred.',
                }),
                description: intl.formatMessage({
                    id: 'global.try_again',
                    defaultMessage: 'Please try again.',
                }),
                status: 'error',
                duration: 9000,
                isClosable: true,
            });
            return;
        }

        if (!!profilePictureURL && profilePictureURL !== profile.avatar_object_url) {
            const file = await getFileFromURL(profilePictureURL);
            if (!!file) {
                const success = await uploadAvatar(keycloak.subject, keycloak.token, file);
                if (!success) {
                    toast({
                        title: intl.formatMessage({
                            id: 'global.error_occurred',
                            defaultMessage: 'An error occurred.',
                        }),
                        description: intl.formatMessage({
                            id: 'setup_profile.upload_profile_picture_failed_description',
                            defaultMessage: 'Failed to upload profile picture. Please try again.',
                        }),
                        status: 'error',
                        duration: 9000,
                        isClosable: true,
                    });
                    return;
                }
            }
        } else if (!profilePictureURL && !!profile.avatar_object_url) {
            const success = deleteAvatar(keycloak.subject, keycloak.token);
            if (!success) {
                toast({
                    title: intl.formatMessage({
                        id: 'global.error_occurred',
                        defaultMessage: 'An error occurred.',
                    }),
                    description: intl.formatMessage({
                        id: 'global.delete_profile_picture_failed_description',
                        defaultMessage: 'Failed to delete previous avatar. Please try again.',
                    }),
                    status: 'error',
                    duration: 9000,
                    isClosable: true,
                });
                return;
            }
        }

        if (!!username) {
            profile.username = username;
        }

        if (!!aboutMe) {
            profile.description = aboutMe;
        }

        const resp = await setUserPrivateProfile(keycloak.subject, keycloak.token, profile);
        if (!resp.ok) {
            toast({
                title: intl.formatMessage({
                    id: 'global.error_occurred',
                    defaultMessage: 'An error occurred.',
                }),
                description: intl.formatMessage({
                    id: 'global.try_again',
                    defaultMessage: 'Please try again.',
                }),
                status: 'error',
                duration: 9000,
                isClosable: true,
            });
            return;
        }

        navigate('/hosts');
    };

    useEffect(() => {
        const setup = async () => {
            const profile = await getUserPrivateProfile(keycloak.subject, keycloak.token);
            setProfile(profile);
            setProfilePictureURL(profile.avatar_object_url);
            setAboutMe(profile?.description);
            setUsername(profile?.username);
        };

        if (!isLoadFromCache) {
            setup();
        }
    }, []);

    const renderSetupProfilePage = () => (
        <Flex className="setup-profile-container">
            <StreamsixBanner className="setup-profile-banner" />
            <Flex className="divider" />
            <UploadImageComponent
                id="setup-profile-upload-profile-picture"
                className={'setup-profile-picture-container'}
                onChange={setProfilePictureURL}
                headerText={[
                    intl.formatMessage({
                        id: 'global.add_profile_pic',
                        defaultMessage: 'Add Profile Pic',
                    }),
                ]}
                footerText={['130 PX * 130 PX']}
                imageURL={profilePictureURL}
            />
            <ProfileInfoComponent
                aboutMe={aboutMe}
                onAboutMeChange={setAboutMe}
                username={username}
                onUsernameChange={setUsername}
            />
            <Button className="setup-profile-button" onClick={onConfirmDetails}>
                <FormattedMessage
                    id="setup_profile.confirm_details"
                    defaultMessage="Confirm Details"
                />
            </Button>
            <Button className="setup-profile-button" onClick={() => navigate('/hosts')}>
                <FormattedMessage id="global.skip_for_now" defaultMessage="Skip for Now" />
            </Button>
        </Flex>
    );

    return (
        <>
            <BrowserView>
                <Flex className="setup-profile-browser-content">{renderSetupProfilePage()}</Flex>
            </BrowserView>
            <MobileView>
                <Flex className="setup-profile-mobile-content">{renderSetupProfilePage()}</Flex>
            </MobileView>
        </>
    );
};
